// @flow

import * as React from 'react';
import {useState} from 'react';

import Box from 'sail/Box';
import Group from 'sail/Group';
import Button, {ButtonGroup} from 'sail/Button';
import Text from 'sail/Text/Text';
import {BodyMono, Body, BodyAlt} from 'sail/Text';
import {InlineLink} from 'sail/InlineLink';
import {defineMessages, FormattedMessage} from 'react-intl';

import './DemoContainer.scss';

const messages = defineMessages({
  cancel: {
    id: 'docsApp.components.demoContainer.cancel',
    description: 'Cancel button',
    defaultMessage: 'Cancel',
  },
  githubSamples: {
    id: 'docsApp.components.demoContainer.githubSamples',
    description: 'GitHub instructions',
    defaultMessage:
      'We have a set of samples on <InlineLink>GitHub</InlineLink> to help you get started.',
  },
  githubContains: {
    id: 'docsApp.components.demoContainer.githubContains',
    description: 'GitHub instructions',
    defaultMessage: 'Contains examples in {joinedLangs}',
  },
  githubSelect: {
    id: 'docsApp.components.demoContainer.githubSelect',
    description: 'GitHub instructions',
    defaultMessage:
      'Open the <BodyAlt>{integration}</BodyAlt> directory after cloning to see this integration.',
  },
  cliInstall: {
    id: 'docsApp.components.demoContainer.cliInstall',
    description: 'CLI instructions',
    defaultMessage:
      'Install the <InlineLink>Stripe CLI</InlineLink>, then run the following in your terminal:',
  },
  cliContains: {
    id: 'docsApp.components.demoContainer.cliContains',
    description: 'CLI instructions',
    defaultMessage:
      'Contains examples in {joinedLangs}. The same will be configured with your Stripe test API key if you have set up the CLI with your Stripe account.',
  },
  cliSelect: {
    id: 'docsApp.components.demoContainer.cliSelect',
    description: 'CLI instructions',
    defaultMessage:
      'Select the <BodyAlt>{integration}</BodyAlt> integration type when prompted by the CLI.',
  },
  building: {
    id: 'docsApp.components.demoContainer.building',
    description: 'Header for section',
    defaultMessage: "What you're building",
  },
  cloneCLI: {
    id: 'docsApp.components.demoContainer.cloneCLI',
    description: 'Label for a button to clone with cli',
    defaultMessage: 'Clone with CLI',
  },
  cloneGithub: {
    id: 'docsApp.components.demoContainer.cloneGithub',
    description: 'Label for a button to clone from github',
    defaultMessage: 'Clone from GitHub',
  },
});

type InstructionType = 'cli' | 'github' | '';

type InstructionViewProps = {|
  name: string,
  integration?: string,
  setInstructions: (InstructionType) => void,
  programmingLanguages: Array<string>,
|};

export const GitHubInstructionView = ({
  name,
  integration,
  setInstructions,
  programmingLanguages,
}: InstructionViewProps) => {
  const joinedLangs = programmingLanguages.join(', ');
  return (
    <Group>
      <Box flex={{alignItems: 'center', justifyContent: 'space-between'}}>
        <Body>
          <FormattedMessage
            {...messages.githubSamples}
            values={{
              InlineLink: (val) => (
                <InlineLink href="https://github.com/stripe-samples">
                  {val}
                </InlineLink>
              ),
            }}
          />
        </Body>
        <Button
          ghost
          icon="cancel"
          hideLabel
          label={<FormattedMessage {...messages.cancel} />}
          onClick={() => {
            setInstructions('');
          }}
        />
      </Box>
      <Box
        margin={{top: 24, bottom: 16}}
        padding={{bottom: 8}}
        divider={{color: 'light', width: 1, side: 'bottom'}}
      >
        <BodyMono>
          $ git clone https://github.com/stripe-samples/{name}
        </BodyMono>
      </Box>
      <Body>
        <FormattedMessage
          {...messages.githubContains}
          values={{
            joinedLangs,
          }}
        />
      </Body>
      {integration && (
        <Body>
          <FormattedMessage
            {...messages.githubSelect}
            values={{
              BodyAlt: (val) => <BodyAlt>{val}</BodyAlt>,
              integration,
            }}
          />
        </Body>
      )}
    </Group>
  );
};

export const CLIInstructionView = ({
  name,
  integration,
  setInstructions,
  programmingLanguages,
}: InstructionViewProps) => {
  const joinedLangs = programmingLanguages.join(', ');
  return (
    <Group>
      <Box flex={{alignItems: 'center', justifyContent: 'space-between'}}>
        <Body>
          <FormattedMessage
            {...messages.cliInstall}
            values={{
              InlineLink: (txt) => (
                <InlineLink href="/docs/stripe-cli">{txt}</InlineLink>
              ),
            }}
          />
        </Body>
        <Button
          ghost
          icon="cancel"
          hideLabel
          label={<FormattedMessage {...messages.cancel} />}
          onClick={() => {
            setInstructions('');
          }}
        />
      </Box>
      <Box
        margin={{top: 24, bottom: 16}}
        padding={{bottom: 8}}
        divider={{color: 'light', width: 1, side: 'bottom'}}
      >
        <BodyMono>$ stripe samples create {name}</BodyMono>
      </Box>
      <Body>
        <FormattedMessage
          {...messages.cliContains}
          values={{
            joinedLangs,
          }}
        />
      </Body>
      {integration && (
        <Box margin={{top: 12}}>
          <Body>
            <FormattedMessage
              {...messages.cliSelect}
              values={{
                BodyAlt: (val) => <BodyAlt>{val}</BodyAlt>,
                integration,
              }}
            />
          </Body>
        </Box>
      )}
    </Group>
  );
};

type InstructionsProps = {|
  type: InstructionType,
  setInstructions: (InstructionType) => void,
  name: string,
  integration?: string,
  programmingLanguages: Array<string>,
|};

export const Instructions = ({
  type,
  setInstructions,
  name,
  integration,
  programmingLanguages,
}: InstructionsProps) => {
  return (
    <Box className="DocsDemo-instructions">
      {type === 'cli' ? (
        <CLIInstructionView
          name={name}
          integration={integration}
          setInstructions={setInstructions}
          programmingLanguages={programmingLanguages}
        />
      ) : (
        <GitHubInstructionView
          name={name}
          integration={integration}
          setInstructions={setInstructions}
          programmingLanguages={programmingLanguages}
        />
      )}
    </Box>
  );
};

type HeaderProps = {|
  setInstructions: (InstructionType) => void,
|};

export const Header = ({setInstructions}: HeaderProps) => {
  return (
    <Box flex={{justifyContent: 'space-between'}} flexGrow={1}>
      <Text color="dark" weight="medium" size={16} lineHeight={20}>
        <FormattedMessage {...messages.building} />
      </Text>
      <ButtonGroup>
        <Button
          className="DocsDemo-button"
          ghost
          size="small"
          label={<FormattedMessage {...messages.cloneGithub} />}
          onClick={() => {
            setInstructions('github');
          }}
        />
        <Button
          className="DocsDemo-button"
          ghost
          size="small"
          label={<FormattedMessage {...messages.cloneCLI} />}
          onClick={() => {
            setInstructions('cli');
          }}
        />
      </ButtonGroup>
    </Box>
  );
};

type ResultComponentProps = {|
  result: any,
  setResult: (any) => void,
|};

type DemoComponentProps = {|
  setResult: (any) => void,
  newDesign?: boolean,
|};

type DemoContainerProps = {|
  result: React.StatelessFunctionalComponent<ResultComponentProps>,
  demo: React.StatelessFunctionalComponent<DemoComponentProps>,
  name: string,
  programmingLanguages?: Array<string>,
  integration?: string,
|};

/*
 * DemoContainer controls displaying download with GitHub / CLI buttons and the base CSS
 * for all our embedded docs demos
 * @param {React.Component} result The UI for the "successful" demo state
 * @param {React.Component} demo The UI for the core demo experience
 * @param {string} name The name of the sample this demo is associated with
 * @param {string} integration Optional integration type if there are more than one in the sample
 */
export const DemoContainer = ({
  result: ResultComponent,
  demo: DemoComponent,
  name,
  integration,
  programmingLanguages,
}: DemoContainerProps) => {
  const [instructions, setInstructions] = useState('');
  const [result, setResult] = useState(null);

  let view = null;
  if (instructions) {
    // A user clicked either the CLI or GitHub button
    view = (
      <Instructions
        type={instructions}
        setInstructions={setInstructions}
        name={name}
        programmingLanguages={
          programmingLanguages || ['PHP', 'Node.js', 'Ruby', 'Java', 'Python']
        }
        integration={integration}
      />
    );
  } else if (result) {
    // A user interacted with the demo in a way that produced a result to show
    view = <ResultComponent result={result} setResult={setResult} />;
  } else {
    // Default demo view
    view = (
      <>
        <Header setInstructions={setInstructions} />
        <DemoComponent setResult={setResult} />
      </>
    );
  }

  return (
    <Box
      className="DocsDemo"
      background="offset"
      margin={{vertical: 20}}
      padding={20}
      flex={{direction: 'column'}}
    >
      {view}
    </Box>
  );
};
